import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, theme, Avatar, Dropdown, Badge, Space, Divider } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FileTextOutlined,
  UserOutlined,
  SearchOutlined,
  BookOutlined,
  SettingOutlined,
  DashboardOutlined,
  BellOutlined,
  LogoutOutlined,
  HomeOutlined,
  FileSearchOutlined,
  EditOutlined,
  StarOutlined,
  DownOutlined,
  MailOutlined,
  GlobalOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import useAuthStore from '../../store/authStore';
import { ROLES, hasRole, hasAnyRole } from '../../constants/roles';
import {globalStyles} from "../../theme";
import { getProfilePictureUrl } from '../../utils/imageUtils';

const { Header, Sider, Content } = Layout;

const MainLayout = ({ children }) => {
  // Set breakpoint for auto-collapsing sidebar
  const SIDEBAR_COLLAPSE_BREAKPOINT = 1080;

  // Set initial collapsed state based on window width
  const initialWindowWidth = window.innerWidth;

  const [collapsed, setCollapsed] = useState(initialWindowWidth <= SIDEBAR_COLLAPSE_BREAKPOINT);
  const [windowWidth, setWindowWidth] = useState(initialWindowWidth);
  const [isAutoCollapse, setIsAutoCollapse] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const {
    token: { colorBgContainer, borderRadiusLG, colorPrimary },
  } = theme.useToken();

  // Update window width when window is resized
  useEffect(() => {
    let resizeTimer;

    const handleResize = () => {
      setWindowWidth(window.innerWidth);

      // Switch back to auto mode after a short delay
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setIsAutoCollapse(true);
      }, 500); // 500ms delay
    };

    window.addEventListener('resize', handleResize);

    // Clean up event listener and timer on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  // Auto-collapse sidebar based on window width
  useEffect(() => {
    if (isAutoCollapse) {
      if (windowWidth <= SIDEBAR_COLLAPSE_BREAKPOINT) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    }
  }, [windowWidth, isAutoCollapse]);

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
    },
  ];

  const handleUserMenuClick = ({ key }) => {
    if (key === 'logout') {
      console.log('Logout clicked');
      logout();
      navigate('/');
    } else {
      navigate(`/${key}`);
    }
  };

  const logoStyle = {
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: collapsed ? 'center' : 'flex-start',
    padding: collapsed ? '0' : '0 24px',
    color: '#fff',
    fontSize: collapsed ? '14px' : '18px',
    fontWeight: 'bold',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  };

  // Create base menu items with static structure
  const baseMenuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: 'publications-header',
      label: 'Publications',
      type: 'group',
    },
    {
      key: 'my-submissions',
      icon: <EditOutlined />,
      label: 'My Submissions',
      style: { paddingLeft: '48px' },
    },
    {
      key: 'journals-header',
      label: 'Journals',
      type: 'group',
    },
    {
      key: 'journals',
      icon: <FileSearchOutlined />,
      label: 'Browse Journals',
      style: { paddingLeft: '48px' },
    },
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
  ];

  // Create the final menu items array
  const menuItems = [...baseMenuItems];

  // Add Publications submenu items based on role
  const publicationsIndex = menuItems.findIndex(item => item.key === 'my-submissions');

  // Add Journal Manuscripts for CHIEF_EDITOR (after My Submissions)
  if (hasRole(user, ROLES.CHIEF_EDITOR)) {
    menuItems.splice(publicationsIndex + 1, 0, {
      key: 'journal-manuscripts',
      icon: <FileSearchOutlined />,
      label: 'Journal Manuscripts',
      style: { paddingLeft: '48px' },
    });
  }

    if (hasAnyRole(user, [ROLES.CHIEF_EDITOR, ROLES.ADMIN])) {
        menuItems.splice(publicationsIndex + 2, 0, {
            key: 'publications',
            icon: <StarOutlined />,
            label: 'Manage Publications',
            style: { paddingLeft: '48px' },
        });
    }

  // Add Journals submenu items based on role
  const journalsIndex = menuItems.findIndex(item => item.key === 'journals');
  let insertPosition = journalsIndex + 1;

  // Add Manage Journals for ADMIN, CHIEF_EDITOR
  if (hasAnyRole(user, [ROLES.ADMIN, ROLES.CHIEF_EDITOR])) {
    menuItems.splice(insertPosition, 0, {
      key: 'journals/manage',
      icon: <SettingOutlined />,
      label: 'Manage Journals',
      style: { paddingLeft: '48px' },
    });
    insertPosition++;
  }

  // Add Volume Management for EDITOR, CHIEF_EDITOR
  if (hasAnyRole(user, [ROLES.EDITOR, ROLES.CHIEF_EDITOR])) {
    menuItems.splice(insertPosition, 0, {
      key: 'journals/volumes',
      icon: <BookOutlined />,
      label: 'Volume Management',
      style: { paddingLeft: '48px' },
    });
    insertPosition++;
  }

  // Add Issue Management for EDITOR, CHIEF_EDITOR
  if (hasAnyRole(user, [ROLES.EDITOR, ROLES.CHIEF_EDITOR])) {
    menuItems.splice(insertPosition, 0, {
      key: 'journals/issues',
      icon: <FileTextOutlined />,
      label: 'Issue Management',
      style: { paddingLeft: '48px' },
    });
    insertPosition++;
  }

  // Add Journal Approvals for ADMIN, CHIEF_EDITOR
  if (hasAnyRole(user, [ROLES.ADMIN, ROLES.CHIEF_EDITOR])) {
    menuItems.splice(insertPosition, 0, {
      key: 'journals/approvals',
      icon: <StarOutlined />,
      label: 'Journal Approvals',
      style: { paddingLeft: '48px' },
    });
    insertPosition++;
  }

  // Add DOI Management for CHIEF_EDITOR
  if (hasAnyRole(user, [ROLES.CHIEF_EDITOR, ROLES.MANAGING_CHIEF_EDITOR])) {
    menuItems.splice(insertPosition, 0, {
      key: 'doi/management',
      icon: <DatabaseOutlined />,
      label: 'DOI Management',
      style: { paddingLeft: '48px' },
    });
  }

  // Add Reviews menu item only if user has appropriate role
  if (hasAnyRole(user, [ROLES.REVIEWER, ROLES.EDITOR, ROLES.CHIEF_EDITOR, ROLES.MANAGING_CHIEF_EDITOR])) {
    menuItems.push({
      key: 'reviews',
      icon: <EditOutlined />,
      label: 'Reviews',
    });
  }

  // Add admin menu items only if user has ADMIN role
  if (hasAnyRole(user, [ROLES.ADMIN, ROLES.MANAGING_CHIEF_EDITOR])) {
    menuItems.push({
      key: 'admin-header',
      label: 'Admin',
      type: 'group',
    });
    menuItems.push({
      key: 'admin/configuration',
      icon: <SettingOutlined />,
      label: 'Configuration',
      style: { paddingLeft: '48px' },
    });
    menuItems.push({
      key: 'admin/users',
      icon: <UserOutlined />,
      label: 'User Management',
      style: { paddingLeft: '48px' },
    });
  }

    if (hasAnyRole(user, [ROLES.ADMIN])) {
        menuItems.push({
            key: 'admin/doi',
            icon: <DatabaseOutlined />,
            label: 'DOI Management',
            style: { paddingLeft: '48px' },
        });
    }

    // Add Committee Management for ADMIN and MANAGING_CHIEF_EDITOR
    if (hasAnyRole(user, [ROLES.ADMIN, ROLES.MANAGING_CHIEF_EDITOR])) {
        menuItems.push({
            key: 'admin/committee',
            icon: <GlobalOutlined />,
            label: 'Committee',
            style: { paddingLeft: '48px' },
        });
    }

  const handleMenuClick = ({ key }) => {
    // Don't navigate for group headers
    if (key.endsWith('-header')) {
      return;
    }
    navigate(`/${key}`);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={260}
        collapsedWidth={80}
        style={{
          boxShadow: '2px 0 20px 0 rgba(0,0,0,0.1)',
          background: 'linear-gradient(180deg, #001529 0%, #002140 50%, #001529 100%)',
          borderRight: '1px solid rgba(255, 255, 255, 0.05)'
        }}
      >
        <div style={{
          height: '70px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? '0' : '0 20px',
          background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
          margin: '12px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(24, 144, 255, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          whiteSpace: 'nowrap',
          overflow: 'hidden'
        }}>
          {collapsed ? (
            <div style={{
              color: '#fff',
              fontSize: '16px',
              fontWeight: '700',
              letterSpacing: '1px',
              textShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}>
              JMS
            </div>
          ) : (
            <div style={{
              color: '#fff',
              fontSize: '18px',
              fontWeight: '700',
              letterSpacing: '0.5px',
              textShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}>
              NSUK-JMS
            </div>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          defaultSelectedKeys={[location.pathname.split('/')[1] || 'browse']}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            border: 'none',
            padding: '12px 0',
            overflow: 'hidden'
          }}
          className={`sidebar-menu ${collapsed ? 'collapsed-menu' : ''}`}
        />
        <style>
          {`
            .sidebar-menu.ant-menu-dark {
              background: #001529;
              border: none;
            }
            .sidebar-menu.ant-menu-dark .ant-menu-sub {
              background: #000c17 !important;
              border-radius: 8px;
              margin: 0 12px;
              box-shadow: inset 0 2px 8px rgba(0,0,0,0.15);
            }
            .sidebar-menu.ant-menu-dark .ant-menu-item {
              height: 48px;
              line-height: 48px;
              margin: 6px 12px;
              border-radius: 12px;
              padding: 0 20px !important;
              width: calc(100% - 24px);
              transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              position: relative;
              overflow: hidden;
            }
            .sidebar-menu.ant-menu-dark .ant-menu-submenu-title {
              height: 48px;
              line-height: 48px;
              margin: 6px 12px;
              border-radius: 12px;
              padding: 0 20px !important;
              width: calc(100% - 24px);
              transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              position: relative;
              overflow: hidden;
            }

            /* Hover effects with modern gradients */
            .sidebar-menu.ant-menu-dark .ant-menu-item:hover {
              background: linear-gradient(135deg, rgba(24, 144, 255, 0.1) 0%, rgba(9, 109, 217, 0.15) 100%) !important;
              transform: translateX(4px);
              box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
            }
            .sidebar-menu.ant-menu-dark .ant-menu-submenu-title:hover {
              background: linear-gradient(135deg, rgba(24, 144, 255, 0.1) 0%, rgba(9, 109, 217, 0.15) 100%) !important;
              transform: translateX(4px);
              box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
            }

            /* Active/Selected states */
            .sidebar-menu.ant-menu-dark .ant-menu-item-selected {
              background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%) !important;
              font-weight: 600;
              box-shadow: 0 4px 16px rgba(24, 144, 255, 0.25);
              transform: translateX(4px);
            }
            .sidebar-menu.ant-menu-dark .ant-menu-item-selected::before {
              content: '';
              position: absolute;
              left: 0;
              top: 50%;
              transform: translateY(-50%);
              width: 4px;
              height: 20px;
              background: #fff;
              border-radius: 0 2px 2px 0;
            }

            /* Submenu items */
            .sidebar-menu.ant-menu-dark .ant-menu-sub .ant-menu-item {
              height: 44px;
              line-height: 44px;
              margin: 4px 16px;
              padding: 0 16px !important;
              width: calc(100% - 32px);
              border-radius: 8px;
              background: rgba(255, 255, 255, 0.02);
            }
            .sidebar-menu.ant-menu-dark .ant-menu-sub .ant-menu-item:hover {
              background: rgba(24, 144, 255, 0.1) !important;
              transform: translateX(2px);
            }
            .sidebar-menu.ant-menu-dark .ant-menu-sub .ant-menu-item-selected {
              background: rgba(24, 144, 255, 0.2) !important;
              color: #1890ff;
              font-weight: 500;
            }

            /* Icons */
            .sidebar-menu.ant-menu-dark .ant-menu-item .anticon,
            .sidebar-menu.ant-menu-dark .ant-menu-submenu-title .anticon {
              font-size: 18px;
              vertical-align: middle;
              min-width: 20px;
              color: rgba(255, 255, 255, 0.85);
              transition: all 0.3s ease;
            }
            .sidebar-menu.ant-menu-dark .ant-menu-item:hover .anticon,
            .sidebar-menu.ant-menu-dark .ant-menu-submenu-title:hover .anticon {
              color: #1890ff;
              transform: scale(1.1);
            }
            .sidebar-menu.ant-menu-dark .ant-menu-item-selected .anticon {
              color: #fff;
              transform: scale(1.1);
            }

            /* Submenu icons */
            .sidebar-menu.ant-menu-dark .ant-menu-sub .ant-menu-item .anticon {
              font-size: 16px;
              color: rgba(255, 255, 255, 0.7);
            }
            .sidebar-menu.ant-menu-dark .ant-menu-sub .ant-menu-item:hover .anticon {
              color: #1890ff;
            }

            /* Text spacing */
            .sidebar-menu.ant-menu-dark .ant-menu-item-icon + span,
            .sidebar-menu.ant-menu-dark .ant-menu-submenu-title .ant-menu-title-content {
              margin-left: 14px;
              font-weight: 500;
            }

            /* Collapsed menu styles */
            .collapsed-menu.ant-menu-inline-collapsed .ant-menu-item {
              padding: 0 calc(50% - 20px) !important;
              text-align: center;
              margin: 6px 16px;
              width: calc(100% - 32px);
            }
            .collapsed-menu.ant-menu-inline-collapsed .ant-menu-submenu-title {
              padding: 0 calc(50% - 20px) !important;
              text-align: center;
              margin: 6px 16px;
              width: calc(100% - 32px);
            }
            .collapsed-menu.ant-menu-inline-collapsed .ant-menu-item .anticon,
            .collapsed-menu.ant-menu-inline-collapsed .ant-menu-submenu-title .anticon {
              margin: 0;
              font-size: 20px;
              line-height: 48px;
            }

            /* Submenu arrow styling */
            .sidebar-menu.ant-menu-dark .ant-menu-submenu-arrow {
              color: rgba(255, 255, 255, 0.6);
              transition: all 0.3s ease;
            }
            .sidebar-menu.ant-menu-dark .ant-menu-submenu:hover .ant-menu-submenu-arrow {
              color: #1890ff;
            }

            /* Menu group headers */
            .sidebar-menu.ant-menu-dark .ant-menu-item-group-title {
              color: rgba(255, 255, 255, 0.9) !important;
              font-size: 14px !important;
              font-weight: 600 !important;
              padding: 16px 20px 8px 20px !important;
              margin: 16px 12px 0 12px !important;
              border-bottom: 1px solid rgba(255, 255, 255, 0.1);
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }

            .sidebar-menu.ant-menu-dark .ant-menu-item-group-title:first-child {
              margin-top: 8px !important;
            }

            /* Indented menu items */
            .sidebar-menu.ant-menu-dark .ant-menu-item[style*="paddingLeft"] {
              margin-left: 24px !important;
              width: calc(100% - 48px) !important;
              background: rgba(255, 255, 255, 0.02) !important;
              border-left: 2px solid rgba(255, 255, 255, 0.1);
            }

            .sidebar-menu.ant-menu-dark .ant-menu-item[style*="paddingLeft"]:hover {
              background: linear-gradient(135deg, rgba(24, 144, 255, 0.15) 0%, rgba(9, 109, 217, 0.2) 100%) !important;
              border-left-color: #1890ff;
            }

            .sidebar-menu.ant-menu-dark .ant-menu-item-selected[style*="paddingLeft"] {
              background: linear-gradient(135deg, rgba(24, 144, 255, 0.3) 0%, rgba(9, 109, 217, 0.35) 100%) !important;
              border-left-color: #fff;
              color: #1890ff !important;
              font-weight: 600;
            }

            /* Custom scrollbar for menu */
            .sidebar-menu::-webkit-scrollbar {
              width: 4px;
            }
            .sidebar-menu::-webkit-scrollbar-track {
              background: rgba(255, 255, 255, 0.05);
            }
            .sidebar-menu::-webkit-scrollbar-thumb {
              background: rgba(255, 255, 255, 0.2);
              border-radius: 2px;
            }
            .sidebar-menu::-webkit-scrollbar-thumb:hover {
              background: rgba(255, 255, 255, 0.3);
            }
          `}
        </style>
      </Sider>
      <Layout style={{ background: '#ffffff' }}>
        <Header
          style={{
            padding: '0 24px',
            background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
            boxShadow: '0 2px 20px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.1)',
            borderBottom: '1px solid rgba(5, 5, 5, 0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backdropFilter: 'blur(8px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => {
                setCollapsed(!collapsed);
                setIsAutoCollapse(false);
              }}
              style={{
                fontSize: '18px',
                width: 48,
                height: 48,
                borderRadius: '12px',
                border: '1px solid rgba(0,0,0,0.06)',
                background: 'rgba(255,255,255,0.8)',
                color: '#595959',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#1890ff';
                e.target.style.color = '#fff';
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.8)';
                e.target.style.color = '#595959';
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = 'none';
              }}
            />

            {/* Breadcrumb area - can be enhanced later */}
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#262626',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <HomeOutlined style={{ fontSize: '16px', color: '#8c8c8c' }} />
              <span style={{ color: '#8c8c8c' }}>/</span>
              <span>{location.pathname.split('/')[1] || 'Dashboard'}</span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Quick Actions */}
            <Space size="small">
              <Button
                type="text"
                icon={<GlobalOutlined />}
                onClick={() => navigate('/')}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  border: '1px solid rgba(0,0,0,0.06)',
                  background: 'rgba(255,255,255,0.8)',
                  color: '#595959',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = globalStyles.gradients.primary2;
                  e.target.style.color = '#fff';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(82, 196, 26, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.8)';
                  e.target.style.color = '#595959';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              />

              <Badge count={5} size="small" style={{
                boxShadow: '0 0 0 1px #fff',
              }}>
                <Button
                  type="text"
                  icon={<BellOutlined />}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    background: 'rgba(255,255,255,0.8)',
                    color: '#595959',
                    fontSize: '16px',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#ff4d4f';
                    e.target.style.color = '#fff';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(255, 77, 79, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.8)';
                    e.target.style.color = '#595959';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </Badge>

              <Button
                type="text"
                icon={<MailOutlined />}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  border: '1px solid rgba(0,0,0,0.06)',
                  background: 'rgba(255,255,255,0.8)',
                  color: '#595959',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#722ed1';
                  e.target.style.color = '#fff';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(114, 46, 209, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.8)';
                  e.target.style.color = '#595959';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </Space>

            <Divider type="vertical" style={{
              height: '24px',
              backgroundColor: 'rgba(0,0,0,0.1)',
              margin: '0 8px'
            }} />

            {/* User Profile Dropdown */}
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: handleUserMenuClick
              }}
              placement="bottomRight"
              trigger={['click']}
            >
              <div style={{
                cursor: 'pointer',
                padding: '6px 12px',
                borderRadius: '12px',
                background: 'rgba(255,255,255,0.9)',
                border: '1px solid rgba(0,0,0,0.06)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(24, 144, 255, 0.08)';
                e.currentTarget.style.borderColor = 'rgba(24, 144, 255, 0.2)';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(24, 144, 255, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
                e.currentTarget.style.borderColor = 'rgba(0,0,0,0.06)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
              }}
              >
                <Avatar
                  size={32}
                  icon={<UserOutlined />}
                  src={getProfilePictureUrl(user?.profilePicture)}
                  style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    border: '2px solid rgba(255,255,255,0.9)',
                    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.2)'
                  }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span style={{
                    color: '#262626',
                    fontSize: '14px',
                    fontWeight: '600',
                    lineHeight: '1.2'
                  }}>
                    {user?.firstName || 'User'} {user?.lastName || ''}
                  </span>
                  <span style={{
                    color: '#8c8c8c',
                    fontSize: '12px',
                    lineHeight: '1.2'
                  }}>
                    {user?.roles?.length > 1 ? `${user.roles[0]} +${user.roles.length - 1}` : (user?.roles?.[0] || user?.role || 'User')}
                  </span>
                </div>
                <DownOutlined style={{
                  fontSize: '12px',
                  color: '#8c8c8c',
                  transition: 'transform 0.3s ease'
                }} />
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            margin: '0 16px 24px',
            padding: '0 24px 24px 24px',
            background: '#fff !!important',
            borderRadius: borderRadiusLG,
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
